import { describeParticipant, type Participant } from "../lib/participants";

/**
 * Who was on this thread and is not on this reply.
 *
 * The question the desk could not answer by looking: an ordinary reply goes to
 * whoever wrote last and carries no Cc, so everybody the customer had copied
 * in silently dropped off it. A thread's participants are known -- they are the
 * To and Cc of every message on it -- so anybody on neither line of *this*
 * reply can be named, with one click to copy them back in. Pulse's version of
 * Gmail's "Reply all", offered after the fact rather than chosen up front,
 * because on this desk the narrow reply is the common one.
 *
 * This used to be the bottom line of a read-only panel that also restated the
 * To and Cc. The To line is now the editable chips themselves, the way Gmail
 * draws it -- every address removable with its own cross, the sender
 * included, and more added by typing -- so a second copy of the same list
 * above it would only have been a way of being unclear about which one counts.
 *
 * Deliberately not styled as an error. Replying to one person off a busy
 * thread is an ordinary, often correct thing to do -- the failure this fixes is
 * not knowing, not the narrowing itself.
 */
export function MissingRecipients({
  missing,
  onAddMissing,
}: {
  /** On the thread, on neither line of this reply. */
  missing: Participant[];
  onAddMissing: () => void;
}) {
  if (missing.length === 0) return null;

  return (
    <div className="mb-2 flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-xs">
      <span className="text-amber-700 dark:text-amber-300">
        {missing.length === 1
          ? "1 person on this thread is not on this reply:"
          : `${missing.length} people on this thread are not on this reply:`}
      </span>
      <span className="min-w-0 text-muted">
        {missing.map(describeParticipant).join(", ")}
      </span>
      <button
        type="button"
        onClick={onAddMissing}
        className="font-medium text-brand-600 hover:underline dark:text-brand-400"
      >
        Copy them in
      </button>
    </div>
  );
}
